import { useState } from 'react';
import { Box, Text, Flex, Button, ActionIcon, Modal, Group, Tooltip } from '@mantine/core';
import { IconX, IconUser, IconMicrophone } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import type { CalendarEvent } from 'src/meetings/models';
import { formatEventBannerDate } from 'src/meetings/utils/date-utils';
import { GuestSummary } from './GuestSummary';
import { PreMeetingInformation } from './PreMeetingInformation';
import { useForceBotJoin } from 'src/meetings/hooks/useForceBotJoin';
import { useBotStatus } from 'src/meetings/hooks/useBotStatus';
import { Mixpanel } from 'src/tracking/mixpanel/Mixpanel';
import { MixpanelEvents } from 'src/tracking/mixpanel/MixpanelEvents';

type Props = {
  event: CalendarEvent;
  onClose: () => void;
};

export const EventOverview = ({ event, onClose }: Props) => {
  const bannerText = formatEventBannerDate(event.scheduledStartAt, event.scheduledEndAt);
  const { forceBotJoin, isLoading } = useForceBotJoin(event.id);
  const { botStatus, isLoading: isBotStatusLoading } = useBotStatus(event.id, event);
  const [isModalOpen, setIsModalOpen] = useState(false);

  /**
   * Triggers the bot to join the meeting immediately
   */
  const handleForceBotJoin = async () => {
    try {
      await forceBotJoin();
      notifications.show({
        title: 'Bot joining meeting',
        message: 'The meeting bot will join within 10 seconds',
        color: 'green',
      });
    } catch {
      notifications.show({
        title: 'Failed to schedule bot',
        message: 'Could not reschedule the bot to join now. Please try again.',
        color: 'red',
      });
    }
  };

  /**
   * Opens the confirmation modal when user clicks "Join meeting"
   * Only shows modal if joining >2 minutes early/late from scheduled time AND bot is scheduled
   */
  const handleJoinMeetingClick = () => {
    const now = new Date();
    const scheduledStart = new Date(event.scheduledStartAt);
    const minutesFromStart = (now.getTime() - scheduledStart.getTime()) / 1000 / 60;

    Mixpanel.track(MixpanelEvents.BOT_JOIN_MEETING_CLICKED, {
      botStatus: botStatus || 'UNKNOWN',
      minutesFromStart: Math.abs(minutesFromStart),
      meetingUrl: event.meetingUrl,
    });

    // If bot is ACTIVE, just open meeting (don't reschedule active bot)
    if (botStatus === 'ACTIVE') {
      if (event.meetingUrl) {
        window.open(event.meetingUrl, '_blank');
      }
      return;
    }

    // If within ±2 minutes of scheduled start, just open meeting without modal
    if (Math.abs(minutesFromStart) <= 2) {
      if (event.meetingUrl) {
        window.open(event.meetingUrl, '_blank');
      }
      return;
    }

    // If bot is NONE or FINISHED, no need to ask about rescheduling
    if (botStatus === 'NONE' || botStatus === 'FINISHED') {
      if (event.meetingUrl) {
        window.open(event.meetingUrl, '_blank');
      }
      return;
    }

    // Otherwise (bot is SCHEDULED and joining early/late), show modal to ask about rescheduling
    setIsModalOpen(true);
  };

  /**
   * Handles the modal confirmation
   * @param rescheduleBot - Whether to reschedule the bot before joining
   */
  const handleConfirmJoin = async (rescheduleBot: boolean) => {
    Mixpanel.track(MixpanelEvents.BOT_JOIN_MEETING_CONFIRMED, {
      rescheduleBot: rescheduleBot,
      meetingUrl: event.meetingUrl,
    });
    if (rescheduleBot) {
      await handleForceBotJoin();
    }

    setIsModalOpen(false);

    // Open meeting URL regardless of bot rescheduling choice
    if (event.meetingUrl) {
      window.open(event.meetingUrl, '_blank');
    }
  };

  /**
   * Handles the "Add Bot" button click - forces bot to join without opening meeting URL
   */
  const handleAddBotClick = async () => {
    Mixpanel.track(MixpanelEvents.BOT_MANUALLY_ADDED_TO_MEETING);
    await handleForceBotJoin();
  };

  return (
    <>
      <Modal
        opened={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Reschedule meeting bot?"
        centered
      >
        <Text size="textMd" mb="xs">
          Do you want to reschedule the bot to join the meeting immediately?
        </Text>
        <Text size="textSm" c="gray.6" mb="lg">
          If the meeting is starting early or you want the bot to join now, select &quot;Yes&quot;.
          Otherwise, the bot will join at the scheduled time.
        </Text>

        <Group justify="flex-end" gap="sm">
          <Button variant="subtle" onClick={() => handleConfirmJoin(false)} disabled={isLoading}>
            No, just join
          </Button>
          <Button variant="primary" onClick={() => handleConfirmJoin(true)} loading={isLoading}>
            Yes, reschedule bot
          </Button>
        </Group>
      </Modal>

      <Box>
        <Flex justify="space-between" align="flex-start" mb="lg">
          <Box
            bg="brand.1"
            py="xxxs"
            px="xxs"
            style={{
              borderRadius: '4px',
              display: 'inline-block',
            }}
          >
            <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
              Upcoming: {bannerText}
            </Text>
          </Box>

          <ActionIcon variant="subtle" onClick={onClose} c="gray.10">
            <IconX size={16} />
          </ActionIcon>
        </Flex>

        <Text size="displaySm" fw={FONT_WEIGHTS.semibold} c="gray.10" mb="lg">
          {event.title}
        </Text>

        <Flex justify="space-between" align="center" mb="lg">
          <Flex align="center" gap="xxxs">
            <IconUser size={16} />
            <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
              <GuestSummary event={event} />
            </Text>
          </Flex>

          <Group gap="xs">
            {event.meetingUrl && (
              <Button variant="primary" onClick={handleJoinMeetingClick} disabled={isLoading}>
                Join meeting
              </Button>
            )}

            {!event.meetingUrl && (
              <Tooltip label={'This meeting does not seem to have a video call link'}>
                <Button variant="primary" disabled>
                  Join meeting
                </Button>
              </Tooltip>
            )}

            <Tooltip
              label={'This meeting does not seem to have a video call link'}
              disabled={!!event.meetingUrl}
            >
              <Button
                variant="outline"
                style={{ borderRadius: 12 }}
                onClick={handleAddBotClick}
                loading={isLoading || isBotStatusLoading}
                disabled={botStatus === 'ACTIVE' || !event.meetingUrl}
                title={
                  botStatus === 'ACTIVE' ? 'Bot is already recording' : 'Force bot to join now'
                }
              >
                {botStatus === 'ACTIVE' ? 'Bot Recording' : 'Add Bot'}
              </Button>
            </Tooltip>
          </Group>
        </Flex>

        {event.hasTranscription && (
          <Flex align="center" gap="xxxs" mb="lg">
            <IconMicrophone size={16} color="var(--mantine-color-brand-6)" />
            <Text size="textSm" fw={FONT_WEIGHTS.medium} c="brand.6">
              Recording received. Details will be available once the meeting time has passed.
            </Text>
          </Flex>
        )}

        <PreMeetingInformation event={event} />
      </Box>
    </>
  );
};
